<template>
    <b-form-group v-show="aa.available" class="expression_row atlas-assistant-container mb-1">
        <div class="row">
            <div class="fixed ml-3">
                <b-img src="/assets/img/png-logo.png" style="width:20px;height:20px;" />
            </div>
            <div class="col pl-2">
                <legend class="bv-no-focus-ring col-form-label pt-0">
                    {{ assistantLabel || 'Atlas Assistant' }}
                    <font-awesome-icon :icon="['fas', 'info-circle']"
                        v-b-tooltip.hover="'Describe the logic that will generate the required output value in plain language to direct the Assistant to construct the corresponding code. The Assistant is powered by AI and responses should be checked before putting into use.'"
                        class="ml-1" />

                </legend>
            </div>
        </div>
        <div class="search__wrapper row">
            <div class="col">
                <b-textarea v-model="aa.question" placeholder="Question" max-rows="3" debounce="300"
                    :disabled="aa.asking" class="p-1 pl-2 pr-2" style="overflow-y:scroll;" />
            </div>
            <div class="fixed pr-3">
                <!-- Ask button -->
                <span :title="askButtonDisabled ? '' : 'Send question to Assistant.'" v-b-tooltip.hover>
                    <b-button id="askAIQuestion" variant="outline-info" :disabled="askButtonDisabled"
                        @click.prevent="askAIQuestion">
                        Ask
                    </b-button>
                </span>
            </div>
        </div>
        <content-spinner :show="aa.asking" />
        <div class="card aaResponse mt-2 p-2 pt-0 pr-0" v-show="responseAvailable && !aa.asking">
            <legend class="bv-no-focus-ring col-form-label pt-1">Generated Formula</legend>
            <div class="row mb-1">
                <div class="col">
                    <code-editor class="aaGeneratedFormula" :document="aa.response.formula" language="javascript"
                        :read-only="true" />
                </div>
                <div class="fixed pr-3">
                    <!-- Use button -->
                    <span :title="useButtonDisabled ? '' : 'Apply the assistant generated formula.'" v-b-tooltip.hover>
                        <b-button id="useAIFormula" class="" variant="outline-info" :disabled="useButtonDisabled"
                            @click.prevent="useAIFormula">
                            Use
                        </b-button>
                    </span>
                </div>
            </div>
            <div v-show="!!aa.response.error" class="aaMessage alert alert-danger mt-1 mb-1 pt-2 pb-2">
                <div class="row">
                    <div class="fixed"><strong>Error!</strong></div>
                    <div class="col">{{ aa.response.error }}</div>
                </div>
            </div>
            <div v-show="!!aa.response.warning" class="aaMessage alert alert-warning mt-1 mb-1 pt-2 pb-2">
                <div class="row">
                    <div class="fixed"><strong>Warning!</strong></div>
                    <div class="col">{{ aa.response.warning }}</div>
                </div>
            </div>
            <div class="aaMessage aaFormulaInfo mt-1">
                {{ aa.response.info }}
            </div>
        </div>
    </b-form-group>
</template>

<script>

import ContentSpinner from '@/core/components/ContentSpinner.vue';
import CodeEditor from './CodeEditor.vue';

export default {

    name: 'AtlasAssistant',

    components: {
        ContentSpinner,
        CodeEditor,
    },

    inject: [
        /* Utility functions etc. [from CalculationModel] */
        'apiUtils',
        'aiUtils',
    ],

    props: [
        /** Alternative label for assistant panel. */
        'assistantLabel',
        /** Assistant query type. */
        'queryType',
        /** Field to host the query result. */
        'targetField',
        /** Fields to include with the query. */
        'queryFields',
        /** Array of instance-specific function names with brief description in brackets. */
        'additionalFunctions',
        /** Flag to log AI conversation to the browser console. */
        'logAssistantConversation',
    ],

    data() {
        return {
            /** Atlas Assistant properties. */
            aa: {
                loaded: false,
                options: null,
                available: false,
                question: '',
                asking: false,
                response: {},
            },
        };
    },

    computed: {

        assistantAvailable() {
            return !!this.aa.available;
        },
        assistantActive() {
            return !!(this.aa.question || '').length || !!(this.aa.response.formula || '').length;
        },
        responseAvailable() {
            return !!(this.aa.response.formula || '').length;
        },
        askButtonDisabled() {
            return !(this.aa.question || '').length || this.aa.asking;
        },
        useButtonDisabled() {
            return !(this.aa.response.formula || '').length;
        },
    },

    methods: {

        // Configuration.
        async loadAIConfig() {
            if (this.aa.loaded) return;
            const response = await this.apiUtils.getFieldCalculationAssistantOptions();
            this.aa.available = response.success && response.body?.options?.assistantAvailable;
            // Function to construct query data must be supplied as prop as this will differ between instances.            
            if (this.aa.available && !this.queryType) {
                console.info('Atlas Assistant unavailable - query type not supplied.');
                this.aa.available = false;
            }
            this.aa.loaded = true;
        },
        async askAIQuestion({ question, currentFormula, formulaError }) {
            if (this.aa.asking) return;
            this.aa.asking = true;
            this.setAAResponse();
            try {
                const questionData = this.aiUtils.constructFieldCalculationAssistantQueryData(
                    {
                        queryType: this.queryType, question: question || this.aa.question,
                        targetField: this.targetField, queryFields: this.queryFields,
                        functions: this.additionalFunctions, currentFormula, formulaError
                    });
                if (this.logAssistantConversation) console.log('questionData:', questionData);

                // Submit question and interpret assistant response.
                const response = await this.apiUtils.askFieldCalculationAssistantQuery(questionData);
                if (this.logAssistantConversation) console.log('Assistant Response:', response);
                this.$set(this.aa.response, 'formula', response.body?.answer);
                if (this.logAssistantConversation) console.log('Assistant Formula:', this.aa.response.formula);
                this.$set(this.aa.response, 'info', response.body?.info);
                this.aa.response.message = response.body?.message;
                this.aa.response.warning = response.body?.warning;
                this.aa.response.error = response.body?.error || response.errorMessage || response.error;

            } finally {
                this.aa.asking = false;
            }
        },

        setAAResponse(data) {
            this.$set(this.aa, 'response', data || {});

        },

        /** Formula has caused an error - attempt to correct it via the Assistant. */
        askFormulaError(currentFormula, formulaError) {
            if (!currentFormula || !formulaError) return;
            this.aa.question = "";
            this.askAIQuestion({ currentFormula: currentFormula, formulaError: formulaError });
        },

        /** Pass the generated formula to the parent component and clear Assistant properties. */
        useAIFormula() {
            this.$emit('use-assistant-formula', this.aa.response.formula);
            this.aa.question = '';
            this.setAAResponse();
        },

    },

    async beforeMount() {
        /* Don't wait for the responses - Panel is only rendered once the data is returned and validated. */
        this.loadAIConfig();
    },

    mounted() {
    },

};

</script>

<style lang="scss" scoped>
.atlas-assistant-container {

    &>div {
        height: 100%;
    }

    #askAIQuestion {
        min-width: 64px;
    }

    .aaGeneratedFormula {
        min-height: 48px;
        max-height: 120px;
        overflow-y: auto;
    }

    .card.aaResponse {
        min-height: 220px;
        max-height: 480px;
        background-color: rgba(0, 0, 0, 0.03);

        .aaMessage {
            max-height: 72px;
            overflow-y: auto;

            &.aaFormulaInfo {
                min-height: 24px;
                max-height: 124px;
                white-space: pre;
                text-wrap-mode: wrap;
            }

            &.alert-danger {
                border-color: #721c2480;
            }

            &.alert-warning {
                border-color: #85640480;
            }
        }

    }
}
</style>
